/**
 * Хук для загрузки изображений через proxy с Authorization.
 *
 * Использует общий MediaLoader для:
 * - ограничения параллельных запросов (макс. 3)
 * - retry при ошибке (3 попытки с задержкой)
 * - LRU-кеширования blob URL (до 120 элементов)
 * - корректного revokeObjectURL при вытеснении из кеша
 */

import { useEffect, useSyncExternalStore } from 'react';
import { useConfigStore } from '@/store/configStore';
import { mediaLoader } from '@/utils/mediaLoader';

export type ImageLoadStatus = 'loading' | 'done' | 'error';

interface ImageProxyResult {
  url: string | null;
  status: ImageLoadStatus;
}

export function useImageProxy(imageUrl: string): ImageProxyResult {
  const { config } = useConfigStore();

  const subscribe = (onStoreChange: () => void) =>
    mediaLoader.subscribe(imageUrl, onStoreChange);

  const getSnapshot = (): string | null => mediaLoader.getCachedUrl(imageUrl);
  const getStatus = (): ImageLoadStatus => mediaLoader.getStatus(imageUrl) ?? 'loading';

  const cachedUrl = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const status = useSyncExternalStore(subscribe, getStatus, getStatus);

  useEffect(() => {
    mediaLoader.load(imageUrl, config.apiKey || undefined);
  }, [imageUrl, config.apiKey]);

  return { url: cachedUrl, status };
}
